import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import ProcedureDropdown from 'studies/typed/shared/ProcedureDropdown';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

interface EditProcedureModalProps {
  procedureId: string;
  flowchartVersionId: string;
  procedureType: ProcedureType;
  setProcedureId: (procedureId: string) => void;
  onRequestClose: () => void;
}

// exported for testing
export const DISABLED_UPDATE_TOOLTIP_TEXT = 'Select a new procedure to update';

export default function EditProcedureModal(props: EditProcedureModalProps): JSX.Element {
  const [newProcedureId, setNewProcedureId] = useState<string | null>(null);

  const procedureUIStringLowercase = getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    props.procedureType,
    {stringCase: StringCase.LOWER}
  );

  const handleUpdate = (): void => {
    if (newProcedureId && newProcedureId !== props.procedureId) {
      props.setProcedureId(newProcedureId);
    }
  };

  return (
    <Modal
      title={`Update ${procedureUIStringLowercase}`}
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: 'Back',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: props.onRequestClose,
        },
        {
          text: 'Update',
          submit: true,
          disabled: !newProcedureId || newProcedureId === props.procedureId,
          tooltipTitle:
            !newProcedureId || newProcedureId === props.procedureId ? DISABLED_UPDATE_TOOLTIP_TEXT : null,
          onClick: handleUpdate,
        },
      ]}
    >
      <div className={css(styles.bannerContainer)}>
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`Changing the ${procedureUIStringLowercase} for your design will remove values from your current design.`}
          supportingInfo="Any values you have for parameters, material inputs, or equipment will be removed."
        />
      </div>

      <div className={css(styles.fieldsContainer)}>
        <div className={css(styles.field)}>
          <FieldLabel label={`Current ${procedureUIStringLowercase}`} />
          <ProcedureChip
            id={props.procedureId}
            flowchartVersionId={props.flowchartVersionId}
            size={ChipSize.SMALL}
          />
        </div>

        <div className={css(styles.field)}>
          <FieldLabel label={`New ${procedureUIStringLowercase}`} />
          <ProcedureDropdown
            isDisabled={false}
            procedureType={props.procedureType}
            selectedProcedureId={newProcedureId}
            onChangeSelectedProcedure={setNewProcedureId}
            onRequestCloseModal={props.onRequestClose}
            procedureFetchPolicy="network-only"
            placeholderText={`Select ${procedureUIStringLowercase}`}
          />
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  bannerContainer: {
    marginBottom: 20,
  },
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 20,
  },
  field: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});
